import {
  InteractionRequiredAuthError,
  type IPublicClientApplication,
  type SilentRequest,
} from "@azure/msal-browser";
import { loginRequest } from "./msalConfig";

/**
 * Re-authenticate instead of dying with a raw error when the Entra session is gone.
 *
 * We cache in localStorage (see msalConfig) so a signed-in user survives closing
 * the tab — important on mobile Safari. The trade-off: the cached account outlives
 * the Entra-side session, which can expire or be revoked independently. When that
 * happens acquireTokenSilent throws InteractionRequiredAuthError (typically
 * AADSTS160021 "Application requested a user session which does not exist").
 *
 * Roughly twenty call sites acquire tokens, and none of them handled that error,
 * so the user was left staring at a red error banner on a dead page instead of
 * being sent back to sign in. Rather than patch every call site, we wrap
 * acquireTokenSilent once here: silent failures that need interaction turn into a
 * redirect to Entra, which either restores the session transparently or shows the
 * sign-in screen.
 *
 * Install BEFORE applyDevAuth: in dev-token mode devAuth replaces
 * acquireTokenSilent outright and this guard is (correctly) bypassed.
 */

const LOOP_KEY = "authRedirectAt";
const LOOP_WINDOW_MS = 20_000;

function needsInteraction(err: unknown): boolean {
  if (err instanceof InteractionRequiredAuthError) return true;
  const e = err as { errorCode?: string; errorMessage?: string; message?: string } | null;
  const text = `${e?.errorCode ?? ""} ${e?.errorMessage ?? ""} ${e?.message ?? ""}`;
  return /interaction_required|login_required|consent_required|no_account_error|no_tokens_found|AADSTS160021|AADSTS50058|AADSTS50078|AADSTS700082/i.test(text);
}

export function installSessionGuard(instance: IPublicClientApplication): void {
  const acquireSilently = instance.acquireTokenSilent.bind(instance);
  let redirecting = false;

  instance.acquireTokenSilent = async (request: SilentRequest) => {
    try {
      const result = await acquireSilently(request);
      // Healthy again — drop the loop-breaker so a later, unrelated expiry still
      // gets a normal re-login redirect rather than an immediate sign-out.
      if (sessionStorage.getItem(LOOP_KEY)) sessionStorage.removeItem(LOOP_KEY);
      return result;
    } catch (err) {
      if (!needsInteraction(err)) throw err;

      if (!redirecting) {
        redirecting = true;

        // If we already bounced through Entra moments ago and are still failing,
        // a plain retry would loop. Sign out instead so the next attempt starts
        // from a clean cache rather than the stale account.
        const last = Number(sessionStorage.getItem(LOOP_KEY) ?? 0);
        const looping = last > 0 && Date.now() - last < LOOP_WINDOW_MS;
        sessionStorage.setItem(LOOP_KEY, String(Date.now()));

        try {
          if (looping) {
            await instance.logoutRedirect();
          } else {
            await instance.acquireTokenRedirect({
              ...loginRequest,
              account: request.account ?? undefined,
            });
          }
        } catch {
          try { await instance.loginRedirect(loginRequest); } catch { /* navigation lost */ }
        }
      }

      // A full-page navigation to Entra is in flight; never hand the caller a
      // token or an error it would render as a failure banner.
      return await new Promise<never>(() => {});
    }
  };
}
